import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isMobile } from 'react-device-detect'
import { useRouteMatch } from 'react-router-dom'
import { generateRandomId, getVideoDevices } from '../helpers/WebcamHelper'
import cameraStreamManager from '../helpers/cameraStreamManager'
import {
  intentId as intentIdSelector,
  recordingRequested,
  orderId as orderIdSelector,
  recordingStarted,
  stopRecording as stopRecordingAction,
} from '../redux/webcamRecorderSlice'
import VideoUploadClient from '../helpers/videoUploadClient'
import { PACKING_PROOF_CONFIG } from '../services/packingProofService'
import { GENERATE_LABELS, GENERATE_ORDER_LABEL } from '../navigation/constants'
import cameraEventQueue from '../helpers/cameraEventQueue'
import { emitCameraStatusEvent } from '../helpers/cameraStatusEmitter'
import { CAMERA_STATUS, CAMERA_STOP_REASON } from '../helpers/cameraStatusConstants'

const VIDEO_MIME = 'video/webm'
const VIDEO_CODECS = 'vp9'
const VIDEO_TIMESLICE = 2000
export const VIDEO_MAXIMUM_RECORDED_SECONDS = 3600

const errorContext = (error, extra = {}) => ({
  error_name: error?.name || null,
  error_message: error?.message || null,
  ...extra,
})

const useWebcamRecorder = () => {
  const dispatch = useDispatch()
  const shouldRecord = useSelector(recordingRequested)
  const orderId = useSelector(orderIdSelector)
  const intentId = useSelector(intentIdSelector)

  // One object per recording session. Everything a session needs (upload
  // client, recorder, stream) lives here and is captured by closure in its
  // event handlers, so a session finalizing late can never touch the state of
  // the session that replaced it (e.g. the next order in a tote).
  const activeSession = useRef(null)
  const [isEnabled, setIsEnabled] = useState(null)

  const recorderIdRef = useRef(null)
  if (recorderIdRef.current === null) {
    recorderIdRef.current = `recorder_${generateRandomId()}`
  }

  const isRouteToKeepRecording = useRouteMatch([GENERATE_LABELS, GENERATE_ORDER_LABEL])

  const buildEventEmitter =
    session =>
    (status, context = null) =>
      emitCameraStatusEvent({
        session,
        status,
        context,
        enqueue: event => cameraEventQueue.enqueue(event),
        dispatch,
      })

  const emitEvent = buildEventEmitter({ orderId, intentId, fileIdentifier: null })

  const markStopReason = (session, reason) => {
    if (session == null) return
    session.stopReason = reason
  }

  const releaseStream = session => {
    if (session.isSubscribed) {
      cameraStreamManager.release(session.subscriberId)
      session.isSubscribed = false
    }
    session.stream = null
  }

  // clearsSession: the branch owns the session's teardown. Otherwise the
  // session stays active and stopRecording() tears it down.
  const failSession = (session, status, context, { clearsSession }) => {
    session.emit(status, context)
    session.stopReason = CAMERA_STOP_REASON.FAILURE
    if (clearsSession && activeSession.current === session) activeSession.current = null
    dispatch(stopRecordingAction({ intentId: session.intentId }))
  }

  const detectRecordingEnabled = async () => {
    if (isMobile) {
      setIsEnabled(false)
      return
    }
    const videoDevices = await getVideoDevices()
    setIsEnabled(videoDevices.length > 0)
  }

  useEffect(() => {
    detectRecordingEnabled()
  }, [])

  useEffect(() => {
    if (shouldRecord === null || shouldRecord === undefined) return
    if (isEnabled === null) return
    if (isEnabled === false) {
      if (shouldRecord) {
        emitEvent(CAMERA_STATUS.NO_DEVICE, { is_mobile: !!isMobile })
        dispatch(stopRecordingAction({ intentId }))
      }
      return
    }
    if (shouldRecord) {
      if (activeSession.current != null && activeSession.current.intentId !== intentId) {
        markStopReason(activeSession.current, CAMERA_STOP_REASON.ROUTE_CHANGE)
        stopRecording()
      }
      startRecording()
    } else {
      stopRecording()
    }
  }, [shouldRecord, isEnabled, intentId])

  useEffect(() => {
    if (shouldRecord && !isRouteToKeepRecording) {
      markStopReason(activeSession.current, CAMERA_STOP_REASON.ROUTE_CHANGE)
      dispatch(stopRecordingAction({ intentId }))
    }
  }, [isRouteToKeepRecording])

  const startRecording = async () => {
    if (activeSession.current != null) {
      return
    }

    const session = {
      orderId,
      intentId,
      // Set when the platform answers the create; emit reads it at call time.
      fileIdentifier: null,
      emit: null,
      // Unique per session: a stale session releasing its subscription must
      // never evict the session that replaced it under a shared id.
      subscriberId: `${recorderIdRef.current}_${generateRandomId()}`,
      uploadClient: null,
      recorder: null,
      stream: null,
      isSubscribed: false,
      startTime: null,
      stopRequested: false,
      stopReason: null,
    }
    session.emit = buildEventEmitter(session)
    activeSession.current = session

    const onStreamChange = (stream, info) => {
      if (info.reason === 'ended' && activeSession.current === session && !session.stopRequested) {
        failSession(session, CAMERA_STATUS.DEVICE_UNAVAILABLE, { reason: 'track_ended' }, { clearsSession: false })
      }
    }

    try {
      session.stream = await cameraStreamManager.subscribe(session.subscriberId, onStreamChange)
      session.isSubscribed = true
    } catch (error) {
      // subscribe() registers the id before acquiring; drop the registration
      // so a failed session cannot keep the camera held open forever.
      cameraStreamManager.release(session.subscriberId)
      const status =
        error?.name === 'NotAllowedError'
          ? CAMERA_STATUS.PERMISSION_DENIED
          : error?.name === 'NotReadableError'
            ? CAMERA_STATUS.DEVICE_UNAVAILABLE
            : CAMERA_STATUS.GET_USER_MEDIA_ERROR
      failSession(session, status, errorContext(error), { clearsSession: true })
      return
    }

    if (session.stopRequested) {
      releaseStream(session)
      return
    }

    // Local binding (not a shared ref): the upload lifecycle of this session
    // stays attached to this session's recorder events only.
    const uploadClient = new VideoUploadClient(PACKING_PROOF_CONFIG, {
      onChunkUploadFailed: info => session.emit(CAMERA_STATUS.UPLOAD_CHUNK_FAILED, info),
      onUploadCompleted: info => session.emit(CAMERA_STATUS.UPLOAD_COMPLETED, info),
      onUploadFailed: info => session.emit(CAMERA_STATUS.UPLOAD_FAILED, info),
    })
    session.uploadClient = uploadClient

    try {
      session.fileIdentifier = await uploadClient.createMultipartUploadForOrder(session.orderId, 5)
    } catch (error) {
      failSession(session, CAMERA_STATUS.UPLOAD_FAILED, errorContext(error, { reason: 'create_multipart_upload_failed' }), { clearsSession: true })
      releaseStream(session)
      return
    }

    if (session.stopRequested) {
      // Stop won the race against startup: nothing recorded. finish() resolves
      // without completing (empty upload) and the backend sweeper aborts it.
      uploadClient.finishUploadingAndCompleteMultipartUpload()
      releaseStream(session)
      return
    }

    const onRecordingDataAvailable = async event => {
      if (event.data?.size > 0) {
        try {
          await uploadClient.appendVideoChunk(event.data)
        } catch (error) {
          // Already reported through onChunkUploadFailed; the chunk cache is
          // kept, so the next append or the final flush retries this part.
        }
      }

      const currentRecordedSeconds = session.startTime ? (new Date() - session.startTime) / 1000 : 0
      if (!session.stopRequested && currentRecordedSeconds > VIDEO_MAXIMUM_RECORDED_SECONDS) {
        markStopReason(session, CAMERA_STOP_REASON.MAX_DURATION)
        dispatch(stopRecordingAction({ intentId: session.intentId }))
      }
    }

    // Fires after the recorder has flushed its final dataavailable event, so
    // by now every chunk is in the upload client: finalizing here is what
    // guarantees the video is completed with its full tail.
    const onRecordingStop = async () => {
      try {
        await uploadClient.finishUploadingAndCompleteMultipartUpload()
      } catch (error) {
        session.emit(CAMERA_STATUS.UPLOAD_FAILED, {
          reason: 'finish_upload_threw',
          error_name: error?.name || null,
          error_message: error?.message || null,
        })
      }
    }

    const onRecordingError = event => {
      failSession(session, CAMERA_STATUS.RECORDING_ERROR, errorContext(event?.error), { clearsSession: false })
    }

    try {
      session.recorder = new MediaRecorder(session.stream, {
        mimeType: `${VIDEO_MIME}; codecs=${VIDEO_CODECS}`,
        audioBitsPerSecond: 0,
        videoBitsPerSecond: 2_500_000,
      })
      session.recorder.addEventListener('dataavailable', onRecordingDataAvailable)
      session.recorder.addEventListener('stop', onRecordingStop)
      session.recorder.addEventListener('error', onRecordingError)
      session.recorder.start(VIDEO_TIMESLICE)
      session.startTime = new Date()
      dispatch(recordingStarted({ intentId: session.intentId, fileIdentifier: session.fileIdentifier, startedAt: session.startTime.toISOString() }))
      session.emit(CAMERA_STATUS.RECORDING_STARTED, {
        mime_type: `${VIDEO_MIME}; codecs=${VIDEO_CODECS}`,
        timeslice_ms: VIDEO_TIMESLICE,
      })
    } catch (error) {
      failSession(session, CAMERA_STATUS.RECORDING_ERROR, errorContext(error), { clearsSession: true })
      uploadClient.finishUploadingAndCompleteMultipartUpload()
      releaseStream(session)
    }
  }

  const stopRecording = () => {
    const session = activeSession.current
    if (session == null) {
      return
    }
    activeSession.current = null
    session.stopRequested = true

    const reason = session.stopReason ?? CAMERA_STOP_REASON.MANUAL
    const durationSeconds = session.startTime ? (new Date() - session.startTime) / 1000 : 0

    if (session.recorder != null && session.recorder.state !== 'inactive') {
      // Listeners stay attached on purpose: stop() flushes one last
      // dataavailable and then fires stop, which finalizes the upload.
      session.recorder.stop()
    }
    // No recorder yet means stop raced startup: the stopRequested checkpoints
    // inside startRecording finalize the upload and release the stream.
    releaseStream(session)

    // Only sessions that actually recorded emit a stopped event, so telemetry
    // keeps pairing every stopped event with a recording_started one.
    if (session.startTime != null) {
      if (reason === CAMERA_STOP_REASON.MAX_DURATION) {
        session.emit(CAMERA_STATUS.RECORDING_STOPPED_TIMEOUT, { duration_seconds: durationSeconds })
      } else {
        session.emit(CAMERA_STATUS.RECORDING_STOPPED_NORMAL, {
          duration_seconds: durationSeconds,
          reason,
        })
      }
    }
  }

  useEffect(
    () => () => {
      const session = activeSession.current
      if (session != null) {
        markStopReason(session, CAMERA_STOP_REASON.UNMOUNT)
        stopRecording()
      }
      dispatch(stopRecordingAction(session ? { intentId: session.intentId } : undefined))
    },
    // unmount only: reads the session through the ref, never render state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
}

export default useWebcamRecorder
